<script lang="ts">
  import { onMount } from "svelte";
  import {
    listUsers as apiListUsers,
    createUser,
    updateUser,
    deleteUser,
    addUserRole,
    removeUserRole,
    resendUserVerification,
    sendUserReset,
    listSchools,
    listAuthorities,
    listRooms,
    setRoomStudents,
    ApiError,
  } from "../lib/api";
  import { push } from "svelte-spa-router";
  import UserLink from "../lib/UserLink.svelte";
  import Breadcrumb from "../lib/Breadcrumb.svelte";
  import { scopes } from "../lib/session.svelte";
  import type {
    AdminUser,
    AdminRoom,
    Authority,
    School,
    RoleGrant,
    CreateUserRequest,
  } from "@diy-avatar/shared/admin";
  import type { Locale, Role } from "@diy-avatar/shared/api";

  let users: AdminUser[] = [];
  let schools: School[] = [];
  let rooms: AdminRoom[] = [];
  let authorities: Authority[] = [];
  let loading = false;
  let error: string | null = null;

  // List filters (client-side). A user matches when ANY of its role grants
  // satisfies every active filter (empty select = no constraint).
  let filterAuthorityId: number | "" = "";
  let filterSchoolId: number | "" = "";
  let filterRole: Role | "" = "";
  let filterEmail = "";

  $: filteredUsers = users.filter((u) => {
    if (filterEmail && !u.email.toLowerCase().includes(filterEmail.toLowerCase())) {
      return false;
    }
    if (filterAuthorityId === "" && filterSchoolId === "" && filterRole === "") {
      return true;
    }
    return u.roles.some(
      (r) =>
        (filterAuthorityId === "" || r.authority_id === filterAuthorityId) &&
        (filterSchoolId === "" || r.school_id === filterSchoolId) &&
        (filterRole === "" || r.role === filterRole),
    );
  });

  // New user form with a role/scope picker.
  let newEmail = "";
  let newPassword = "";
  let newLanguage: Locale = "de";
  let newRole: Role = "teacher";
  let newSchoolId: number | "" = "";
  let newAuthorityId: number | "" = "";
  // When true the new user is created already-verified (no email). When false
  // the backend sends a verification email and the user must verify to log in.
  let newSkipVerification = true;

  function errText(err: unknown, fallback: string): string {
    if (err instanceof ApiError) {
      if (err.message === "last_school_manager") {
        return "Cannot remove the last school-manager of a school.";
      }
      return err.message;
    }
    return err instanceof Error ? err.message : fallback;
  }

  function handleAuth(err: unknown): boolean {
    if (err instanceof ApiError && err.status === 401) {
      push("/");
      return true;
    }
    return false;
  }

  async function load() {
    loading = true;
    try {
      const res = await apiListUsers();
      users = res.users;
      // Avatars are fetched lazily + cached by each UserLink instance.
      const [sc, au, rm] = await Promise.all([
        listSchools().catch(() => ({ schools: [] as School[] })),
        $scopes.isAdmin
          ? listAuthorities().catch(() => ({ authorities: [] as Authority[] }))
          : Promise.resolve({ authorities: [] as Authority[] }),
        listRooms().catch(() => ({ rooms: [] as AdminRoom[] })),
      ]);
      schools = sc.schools;
      authorities = au.authorities;
      rooms = rm.rooms;
      if (newSchoolId === "" && schools.length) newSchoolId = schools[0].id;
      if (newAuthorityId === "" && authorities.length) newAuthorityId = authorities[0].id;
      error = null;
    } catch (err) {
      if (handleAuth(err)) return;
      error = errText(err, "Could not load users.");
    } finally {
      loading = false;
    }
  }

  /** Build the initial role grant for the create form from the picker. */
  function buildGrant(): RoleGrant | null {
    if (newRole === "admin") return { role: "admin" };
    if (newRole === "authority") {
      if (newAuthorityId === "") return null;
      return { role: "authority", authority_id: newAuthorityId };
    }
    if (newSchoolId === "") return null;
    return { role: newRole, school_id: newSchoolId };
  }

  async function doCreate() {
    error = null;
    if (!newEmail.trim()) {
      error = "email required";
      return;
    }
    // Admin accounts still require an explicit initial password.
    if (newRole === "admin" && !newPassword) {
      error = "password required for admin";
      return;
    }
    const grant = buildGrant();
    if (!grant) {
      error = "pick a scope for the role";
      return;
    }
    try {
      const req: CreateUserRequest = {
        email: newEmail.trim(),
        roles: [grant],
        language: newLanguage,
        skip_verification: newSkipVerification,
      };
      // Only include the password when set; omitting it triggers the backend
      // set-password email flow (non-admin roles).
      if (newPassword) req.password = newPassword;
      await createUser(req);
      newEmail = "";
      newPassword = "";
      newLanguage = "de";
      await load();
    } catch (err) {
      if (handleAuth(err)) return;
      error = errText(err, "create failed");
    }
  }

  async function doToggle(u: AdminUser) {
    try {
      await updateUser(u.id, { disabled: !u.disabled });
      await load();
    } catch (err) {
      if (handleAuth(err)) return;
      error = errText(err, "update failed");
    }
  }

  async function doDelete(u: AdminUser) {
    if (!confirm(`Delete ${u.email}?`)) return;
    try {
      await deleteUser(u.id);
      await load();
    } catch (err) {
      if (handleAuth(err)) return;
      error = errText(err, "delete failed");
    }
  }

  async function doResetPassword(u: AdminUser) {
    // E2EE WARNING: an admin password reset rotates the password-derived
    // wrapping key WITHOUT the user's old password, so it DESTROYS their
    // end-to-end-encryption key. They must re-add it (scan/paste) on next login.
    // web-admin has no modal/i18n system → enriched confirm() string. (A shared
    // confirm component could replace this later.)
    const ok = confirm(
      `Reset the password for ${u.email}?\n\n` +
        "WARNING: this DESTROYS the user's end-to-end-encryption key. " +
        "They will need to re-add their key (scan/paste) on next login. " +
        "Previously encrypted media stays unreadable until re-shared.\n\n" +
        "Continue?",
    );
    if (!ok) return;
    const pw = prompt(`New password for ${u.email}?`);
    if (!pw) return;
    try {
      await updateUser(u.id, { password: pw });
      alert("Password reset. The user must re-add their encryption key on next login.");
    } catch (err) {
      if (handleAuth(err)) return;
      error = errText(err, "reset failed");
    }
  }

  async function doResendVerification(u: AdminUser) {
    try {
      await resendUserVerification(u.id);
      alert(`Verification email sent to ${u.email}.`);
    } catch (err) {
      if (handleAuth(err)) return;
      error = errText(err, "resend failed");
    }
  }

  async function doSendReset(u: AdminUser) {
    try {
      await sendUserReset(u.id);
      alert(`Password-reset link sent to ${u.email}.`);
    } catch (err) {
      if (handleAuth(err)) return;
      error = errText(err, "send reset failed");
    }
  }

  // Per-user inline add-role scratch state. `addingUserId` is the user whose
  // add-role picker is open (null = none). The selects mirror the create form.
  let addingUserId: number | null = null;
  let addRoleSel: Role = "teacher";
  let addSchoolSel: number | "" = "";
  let addAuthoritySel: number | "" = "";

  function openAddRole(u: AdminUser) {
    addingUserId = u.id;
    addRoleSel = "teacher";
    addSchoolSel = schools.length ? schools[0].id : "";
    addAuthoritySel = authorities.length ? authorities[0].id : "";
  }

  function cancelAddRole() {
    addingUserId = null;
  }

  /** Build the grant for the inline add-role picker (mirrors buildGrant). */
  function buildAddGrant(): RoleGrant | null {
    if (addRoleSel === "admin") return { role: "admin" };
    if (addRoleSel === "authority") {
      if (addAuthoritySel === "") return null;
      return { role: "authority", authority_id: addAuthoritySel };
    }
    if (addSchoolSel === "") return null;
    return { role: addRoleSel, school_id: addSchoolSel };
  }

  async function addRole(u: AdminUser, grant: RoleGrant) {
    try {
      await addUserRole(u.id, grant);
      addingUserId = null;
      await load();
    } catch (err) {
      if (handleAuth(err)) return;
      error = errText(err, "add role failed");
    }
  }

  function confirmAddRole(u: AdminUser) {
    const grant = buildAddGrant();
    if (!grant) {
      error = "pick a scope for the role";
      return;
    }
    void addRole(u, grant);
  }

  // Per-user inline add-room scratch state. `addingRoomUserId` is the student
  // whose room picker is open (null = none); `addRoomSel` is the chosen room id.
  let addingRoomUserId: number | null = null;
  let addRoomSel: number | "" = "";

  function hasStudentRole(u: AdminUser): boolean {
    return u.roles.some((r) => r.role === "student");
  }

  /** School ids the user is a student in (drives which rooms are assignable). */
  function studentSchoolIds(u: AdminUser): Set<number> {
    const ids = new Set<number>();
    for (const r of u.roles) {
      if (r.role === "student" && r.school_id != null) ids.add(r.school_id);
    }
    return ids;
  }

  /** Rooms in the student's schools that don't already include the student. */
  function candidateRooms(u: AdminUser): AdminRoom[] {
    const ids = studentSchoolIds(u);
    return rooms.filter((r) => ids.has(r.school_id) && !r.students.includes(u.email));
  }

  function roomOptLabel(r: AdminRoom): string {
    return r.name + " @ " + (r.school_name ?? r.school_id);
  }

  function openAddRoom(u: AdminUser) {
    addingRoomUserId = u.id;
    const c = candidateRooms(u);
    addRoomSel = c.length ? c[0].id : "";
  }

  function cancelAddRoom() {
    addingRoomUserId = null;
  }

  async function confirmAddRoom(u: AdminUser) {
    if (addRoomSel === "") {
      error = "pick a room";
      return;
    }
    const room = rooms.find((r) => r.id === addRoomSel);
    if (!room) return;
    try {
      await setRoomStudents(room.id, [...room.students, u.email]);
      addingRoomUserId = null;
      await load();
    } catch (err) {
      if (handleAuth(err)) return;
      error = errText(err, "assign room failed");
    }
  }

  async function dropRole(u: AdminUser, roleId: number) {
    try {
      await removeUserRole(u.id, roleId);
      await load();
    } catch (err) {
      if (handleAuth(err)) return;
      error = errText(err, "remove role failed");
    }
  }

  function roleChip(r: AdminUser["roles"][number]): string {
    if (r.role === "admin") return "admin";
    if (r.role === "authority") return `authority@${r.authority_name ?? r.authority_id}`;
    return `${r.role}@${r.school_name ?? r.school_id}`;
  }

  onMount(load);
</script>

<section>
  <Breadcrumb items={[{ label: "User" }]} />
  <h1>User</h1>
  {#if error}<p class="error" data-testid="error">{error}</p>{/if}

  <div class="new" data-testid="new-user">
    <h2>Create user</h2>
    <div class="row">
      <input type="email" placeholder="email" bind:value={newEmail} data-testid="new-email" />
      <input
        type="text"
        placeholder={newRole === "admin" ? "password" : "password (optional)"}
        bind:value={newPassword}
        data-testid="new-password"
      />
      <select bind:value={newRole} data-testid="new-role">
        {#if $scopes.isAdmin}<option value="admin">admin</option>{/if}
        {#if $scopes.isAdmin}<option value="authority">authority</option>{/if}
        <option value="school-manager">school-manager</option>
        <option value="teacher">teacher</option>
        <option value="student">student</option>
      </select>
      {#if newRole === "authority"}
        <select bind:value={newAuthorityId} data-testid="new-authority">
          {#each authorities as a (a.id)}<option value={a.id}>{a.name}</option>{/each}
        </select>
      {:else if newRole !== "admin"}
        <select bind:value={newSchoolId} data-testid="new-school">
          {#each schools as s (s.id)}<option value={s.id}>{s.name}</option>{/each}
        </select>
      {/if}
      <select bind:value={newLanguage} data-testid="new-language">
        <option value="de">Deutsch</option>
        <option value="en">English</option>
      </select>
      <label class="skip" title="Create already-verified (no verification email)">
        <input
          type="checkbox"
          bind:checked={newSkipVerification}
          data-testid="new-skip-verification"
        />
        skip verification
      </label>
      <button on:click={doCreate} data-testid="new-submit">Create</button>
    </div>
    {#if newRole !== "admin"}
      <p class="hint" data-testid="new-password-hint">
        Leave empty to email the user a set-password link. If set, the user must
        change it on first login.
      </p>
    {/if}
  </div>

  <div class="filters" data-testid="filters">
    {#if $scopes.isAdmin}
      <label>
        Authority
        <select bind:value={filterAuthorityId} data-testid="filter-authority">
          <option value="">all</option>
          {#each authorities as a (a.id)}<option value={a.id}>{a.name}</option>{/each}
        </select>
      </label>
    {/if}
    <label>
      School
      <select bind:value={filterSchoolId} data-testid="filter-school">
        <option value="">all</option>
        {#each schools as s (s.id)}<option value={s.id}>{s.name}</option>{/each}
      </select>
    </label>
    <label>
      Role
      <select bind:value={filterRole} data-testid="filter-role">
        <option value="">all</option>
        <option value="admin">admin</option>
        <option value="authority">authority</option>
        <option value="school-manager">school-manager</option>
        <option value="teacher">teacher</option>
        <option value="student">student</option>
      </select>
    </label>
    <label>
      Email
      <input type="text" placeholder="search email" bind:value={filterEmail} data-testid="filter-email" />
    </label>
  </div>

  <table data-testid="users-table">
    <thead>
      <tr><th>id</th><th>user</th><th>roles</th><th>verified</th><th>disabled</th><th>actions</th></tr>
    </thead>
    <tbody>
      {#each filteredUsers as u (u.id)}
        <tr data-testid="user-row-{u.id}">
          <td>#{u.id}</td>
          <td data-testid="user-avatar-{u.id}">
            <span class="user-cell">
              <UserLink
                id={u.id}
                email={u.email}
                fullName={u.full_name}
                displayName={u.display_name}
                hasImage={u.has_profile_image}
                size={28}
              />
              {#if u.must_change_password}
                <span class="badge-mcp" data-testid="must-change-{u.id}" title="Must change password on next login">must change pw</span>
              {/if}
            </span>
          </td>
          <td>
            <div class="chips">
              {#each u.roles as r (r.id)}
                <span class="chip" data-testid="role-chip-{u.id}-{r.id}">
                  {roleChip(r)}
                  <button class="chip-x" title="remove role" on:click={() => dropRole(u, r.id)}>×</button>
                </span>
              {/each}
              {#if u.roles.length === 0}<span class="muted">—</span>{/if}
              {#if addingUserId === u.id}
                <span class="chip add-role" data-testid="add-role-form-{u.id}">
                  <select bind:value={addRoleSel} data-testid="add-role-select-{u.id}">
                    {#if $scopes.isAdmin}<option value="admin">admin</option>{/if}
                    {#if $scopes.isAdmin}<option value="authority">authority</option>{/if}
                    <option value="school-manager">school-manager</option>
                    <option value="teacher">teacher</option>
                    <option value="student">student</option>
                  </select>
                  {#if addRoleSel === "authority"}
                    <select bind:value={addAuthoritySel} data-testid="add-role-authority-{u.id}">
                      {#each authorities as a (a.id)}<option value={a.id}>{a.name}</option>{/each}
                    </select>
                  {:else if addRoleSel !== "admin"}
                    <select bind:value={addSchoolSel} data-testid="add-role-school-{u.id}">
                      {#each schools as s (s.id)}<option value={s.id}>{s.name}</option>{/each}
                    </select>
                  {/if}
                  <button class="chip-x" title="confirm" on:click={() => confirmAddRole(u)} data-testid="add-role-confirm-{u.id}">✓</button>
                  <button class="chip-x" title="cancel" on:click={cancelAddRole}>×</button>
                </span>
              {:else}
                <button class="chip add-role-toggle" on:click={() => openAddRole(u)} data-testid="add-role-toggle-{u.id}">+ add role</button>
              {/if}
              {#if hasStudentRole(u)}
                {#if addingRoomUserId === u.id}
                  <span class="chip add-role" data-testid="add-room-form-{u.id}">
                    {#if candidateRooms(u).length}
                      <select bind:value={addRoomSel} data-testid="add-room-select-{u.id}">
                        {#each candidateRooms(u) as r (r.id)}<option value={r.id}>{roomOptLabel(r)}</option>{/each}
                      </select>
                      <button class="chip-x" title="confirm" on:click={() => confirmAddRoom(u)} data-testid="add-room-confirm-{u.id}">✓</button>
                    {:else}
                      <span class="muted">no rooms</span>
                    {/if}
                    <button class="chip-x" title="cancel" on:click={cancelAddRoom}>×</button>
                  </span>
                {:else}
                  <button class="chip add-role-toggle" on:click={() => openAddRoom(u)} data-testid="add-room-toggle-{u.id}">+ add room</button>
                {/if}
              {/if}
            </div>
          </td>
          <td data-testid="verified-{u.id}">
            {#if u.email_verified}
              <span class="ok">yes</span>
            {:else}
              <span class="warn">no</span>
            {/if}
          </td>
          <td>{u.disabled ? "yes" : "no"}</td>
          <td class="actions">
            <button on:click={() => doToggle(u)}>{u.disabled ? "enable" : "disable"}</button>
            <button on:click={() => doResetPassword(u)}>reset pw</button>
            <button on:click={() => doSendReset(u)} data-testid="send-reset-{u.id}">send reset link</button>
            {#if !u.email_verified}
              <button on:click={() => doResendVerification(u)} data-testid="resend-{u.id}">resend verify</button>
            {/if}
            <button on:click={() => doDelete(u)}>delete</button>
          </td>
        </tr>
      {/each}
      {#if filteredUsers.length === 0 && !loading}
        <tr><td colspan="6" class="empty">No users.</td></tr>
      {/if}
    </tbody>
  </table>
</section>

<style>
  h1 { margin: 0 0 1rem; }
  h2 { margin: 0 0 0.5rem; font-size: 1rem; }
  .new {
    margin-bottom: 1rem;
    padding: 0.75rem 1rem;
    background: #111827;
    border: 1px solid #1f2937;
    border-radius: 8px;
  }
  .row { display: flex; gap: 0.5rem; flex-wrap: wrap; align-items: center; }
  .hint { margin: 0.5rem 0 0; font-size: 0.8rem; color: #9ca3af; }
  .filters {
    display: flex;
    gap: 0.75rem;
    flex-wrap: wrap;
    align-items: end;
    margin-bottom: 0.75rem;
  }
  .filters label { display: grid; gap: 0.2rem; font-size: 0.8rem; color: #d1d5db; }
  .user-cell { display: inline-flex; align-items: center; gap: 0.4rem; }
  .badge-mcp {
    margin-left: 0.4rem;
    padding: 0.05rem 0.35rem;
    border-radius: 8px;
    font-size: 0.65rem;
    background: #78350f;
    color: #fcd34d;
  }
  .skip { display: inline-flex; align-items: center; gap: 0.3rem; font-size: 0.8rem; color: #d1d5db; }
  .ok { color: #6ee7b7; }
  .warn { color: #fbbf24; }
  input, select {
    padding: 0.4rem 0.5rem;
    background: #0f172a;
    color: #f3f4f6;
    border: 1px solid #1f2937;
    border-radius: 4px;
  }
  button {
    padding: 0.4rem 0.7rem;
    background: #1f2937;
    color: #f3f4f6;
    border: 1px solid #374151;
    border-radius: 4px;
  }
  table { width: 100%; border-collapse: collapse; background: #111827; }
  th, td {
    padding: 0.4rem 0.6rem;
    text-align: left;
    border-bottom: 1px solid #1f2937;
    font-size: 0.85rem;
    vertical-align: middle;
  }
  th { background: #0f172a; color: #9ca3af; }
  .chips { display: flex; flex-wrap: wrap; gap: 0.3rem; }
  .chip {
    display: inline-flex;
    align-items: center;
    gap: 0.2rem;
    background: #1f2937;
    color: #d1d5db;
    padding: 0.1rem 0.4rem;
    border-radius: 10px;
    font-size: 0.75rem;
  }
  .chip-x {
    background: none;
    border: none;
    color: #9ca3af;
    cursor: pointer;
    padding: 0;
    font-size: 0.9rem;
    line-height: 1;
  }
  .chip-x:hover { color: #fca5a5; }
  .add-role-toggle { cursor: pointer; border: 1px dashed #374151; }
  .add-role { gap: 0.3rem; }
  .add-role select { font-size: 0.7rem; padding: 0.05rem 0.2rem; }
  .actions { display: flex; gap: 0.3rem; flex-wrap: wrap; }
  .muted { color: #6b7280; }
  .empty { color: #6b7280; text-align: center; }
  .error { color: #fca5a5; }
</style>
